import {
  ArrowDropDown,
  ArrowRight,
  Delete,
  Link,
  NotificationAdd,
} from '@mui/icons-material'
import {
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  type IconButtonProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { type ReactEventHandler, useMemo, useState } from 'react'
import { useClipboard } from '@/features/clipboard/useClipboard'
import { useSavedSearch } from '@/features/search/hooks/useSavedSearch'
import type { SavedSearch } from '@/features/search/types'

export const SavedSearchManager = () => {
  const { useSavedSearchQuery } = useSavedSearch()
  const { data: savedSearches } = useSavedSearchQuery()

  return (
    <Grid container spacing={2}>
      {savedSearches &&
        savedSearches?.map((i) => (
          <SavedSearchRow key={i.code} savedSearch={i} />
        ))}
    </Grid>
  )
}

const SavedSearchRow = (props: { savedSearch: SavedSearch }) => {
  const { savedSearch } = props
  const criteriaChips = useMemo(() => {
    if (savedSearch.criteria) {
      return savedSearch.criteria.split('|').map((t) => <Chip label={t} />)
    } else {
      return null
    }
  }, [savedSearch.criteria])

  const [showCriteria, setShowCriteria] = useState(false)

  return (
    <Grid
      container
      size={12}
      justifyContent={'start'}
      alignItems={'center'}
      spacing={0}
    >
      <Grid>
        <ShowCriteriaButton
          savedSearch={savedSearch}
          isOpen={showCriteria}
          onClick={() => {
            setShowCriteria(!showCriteria)
          }}
        />
      </Grid>
      <Grid>
        <Typography fontWeight={500} m={0} p={0} variant="body1">
          {savedSearch.name}
        </Typography>
      </Grid>

      <Grid offset="auto">
        <CopyLinkButton savedSearch={savedSearch} />
        <AddNotificationButton savedSearch={savedSearch} />
        <DeleteButton savedSearch={savedSearch} />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {showCriteria ? (
        <>
          <Grid container py={0.5} spacing={0.5} size={12}>
            {criteriaChips}
          </Grid>
        </>
      ) : null}
    </Grid>
  )
}

const ShowCriteriaButton = (
  props: IconButtonProps & { savedSearch: SavedSearch; isOpen: boolean },
) => {
  const { isOpen, ...buttonProps } = props

  return (
    <IconButton title="Show Criteria" {...buttonProps}>
      {isOpen ? <ArrowDropDown /> : <ArrowRight />}
    </IconButton>
  )
}

const CopyLinkButton = (
  props: IconButtonProps & { savedSearch: SavedSearch },
) => {
  const { savedSearch, ...buttonProps } = props
  const { copyToClipboard } = useClipboard()
  const [isOpen, setOpen] = useState(false)

  function createSearchCriteriaUrl(criteria: string): string {
    if (!criteria) return ''
    const params = new URLSearchParams({
      Criteria: criteria,
    })
    const { origin, pathname } = window.location
    return `${origin}${pathname}?${params.toString()}`
  }

  const handleCopy: ReactEventHandler = (_event) => {
    if (!savedSearch) return
    const url = createSearchCriteriaUrl(savedSearch.criteria)
    window.history.replaceState(null, 'Title', url)
    copyToClipboard(url)
    setOpen(true)
    setTimeout(() => setOpen(false), 3000)
  }

  return (
    <div title="Copy Search URL" style={{ display: 'inline' }}>
      <Tooltip
        open={isOpen}
        content="Copied!"
        title="Copied!"
        arrow
        placement="top"
      >
        <IconButton
          title="Copy Search URL"
          onClick={handleCopy}
          {...buttonProps}
        >
          <Link />
        </IconButton>
      </Tooltip>
    </div>
  )
}

const AddNotificationButton = (
  props: IconButtonProps & { savedSearch: SavedSearch },
) => {
  const { savedSearch, ...buttonProps } = props
  const { useAddNotificationMutation } = useSavedSearch()
  const [addNotif, request] = useAddNotificationMutation()
  const { VITE_LEGACY_URL } = import.meta.env

  const handleClick = () => {
    addNotif({ searchString: savedSearch.criteria })
      .unwrap()
      .then(() => {
        window.location.href = `${VITE_LEGACY_URL}/Notifications.aspx?Page=MyNotifications&Src=Search`
      })
  }

  return (
    <IconButton
      title="Add Notification"
      onClick={handleClick}
      disabled={request.isLoading}
      {...buttonProps}
    >
      {request.isLoading ? <CircularProgress size={24} /> : <NotificationAdd />}
    </IconButton>
  )
}

const DeleteButton = (
  props: IconButtonProps & { savedSearch: SavedSearch },
) => {
  const { savedSearch, ...buttonProps } = props
  const { useDeleteSavedSearchMutation } = useSavedSearch()
  const [deleteSearch, request] = useDeleteSavedSearchMutation()

  const handleClick = () => {
    deleteSearch(savedSearch.code)
  }

  return (
    <IconButton
      color="error"
      title="Delete"
      onClick={handleClick}
      disabled={request.isLoading}
      {...buttonProps}
    >
      <Delete />
    </IconButton>
  )
}
